import { CommonModule } from '@angular/common';
import { Component, Input, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MaterialModule } from '../../../../../shared/modules/material/material.module';
import { CdkDragDrop, moveItemInArray, CdkDrag, CdkDropList } from '@angular/cdk/drag-drop';
import { MatSelect } from '@angular/material/select';
import { ContactDetails, ContactType as ContactTypeEnum } from '../../../../../core/models/user.models';


export interface ContactType {
  contactTypeId: string;
  description: string;
}

@Component({
  selector: 'app-contact-information-submenu',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule, FontAwesomeModule, TranslateModule, CdkDropList, CdkDrag],
  templateUrl: './contact-information-submenu.component.html',
  styleUrl: './contact-information-submenu.component.scss'
})
export class ContactInformationSubmenuComponent {
  @Input() contactDetails!: ContactDetails[];

  @ViewChild("contactTypeSelected", { read: MatSelect, static: false }) contactTypeSelect!: MatSelect;

  formGroup!: FormGroup;

  contactTypeDefault: ContactDetails = {
    order: 0,
    contactType: ContactTypeEnum.MAIL,
    value: '',
    active: false
  }

  contactTypes: ContactType[] = [
    {
      contactTypeId: ContactTypeEnum.MAIL,
      description: this.translateService.instant("configuration-page.action-dialog.email")
    },
    {
      contactTypeId: ContactTypeEnum.PHONE,
      description: this.translateService.instant("configuration-page.action-dialog.phone")
    },
    {
      contactTypeId: ContactTypeEnum.MOBILE_PHONE,
      description: this.translateService.instant("configuration-page.action-dialog.mobile-phone")
    },
    {
      contactTypeId: ContactTypeEnum.FAX,
      description: this.translateService.instant("configuration-page.action-dialog.fax")
    },
    {
      contactTypeId: ContactTypeEnum.URL,
      description: this.translateService.instant("configuration-page.action-dialog.url")
    },
  ]

  constructor(private translateService: TranslateService) { }

  ngOnInit() {
    this.formGroup = new FormGroup({
      contactTypesRows: new FormArray([])
    });

    if (this.contactDetails.length) {
      const formArray = this.formGroup.get('contactTypesRows') as FormArray;
      this.contactDetails.map(contact => {
        const contactFormGroup = this.createContactTypeRow(contact)
        formArray.push(contactFormGroup)
      })
    } else {
      this.formGroup = new FormGroup({
        contactTypesRows: new FormArray(
          [this.createContactTypeRow(this.contactTypeDefault)]
        )
      });
    }
  }
  get contactTypesRows() {
    return this.formGroup.get('contactTypesRows') as FormArray<FormGroup>;
  }

  getValueInFormControl(formGroupName: number, formControlName: string): boolean {
    const formArray = this.formGroup.get("contactTypesRows") as FormArray<FormGroup>;
    const formArrayValues = formArray.controls[formGroupName];
    const formControl = formArrayValues.get(formControlName);

    return !!formControl?.value;
  }

  updateChkbxArray(isChecked: boolean, id: string, formGroupName: number, formControlName: string) {
    const formArray = this.formGroup.get("contactTypesRows") as FormArray<FormGroup>;
    const formArrayValues = formArray.controls[formGroupName];
    const formControl = formArrayValues.get(formControlName);

    isChecked ? formControl?.setValue([id]) : formControl?.setValue([]);
  }

  //This function is created because otherwise when you click on the mat-option text (not the mat-checkbox) 
  //the updateChkbxArray functionality is not triggered.
  propagateCheck(recordId: string, formGroupName: number, formControlName: string) {
    const currentState = this.isChecked(recordId, formGroupName, formControlName);
    this.updateChkbxArray(currentState, recordId, formGroupName, formControlName);
  }

  isChecked(recordId: string, formGroupName: number, formControlName: string): boolean {
    const formArray = this.formGroup.get("contactTypesRows") as FormArray<FormGroup>;
    const formArrayValues = formArray.controls[formGroupName];
    const formControlValues = formArrayValues.get(formControlName)?.value as string[];

    return formControlValues?.includes(recordId);
  }

  clearInput(formGroupName: number, formControlName: string) {
    const formArray = this.formGroup.get("contactTypesRows") as FormArray<FormGroup>;
    const formArrayValues = formArray.controls[formGroupName];
    formArrayValues.get(formControlName)?.reset();
  }

  clearInputByKey(event: KeyboardEvent , formGroupName: number, formControlName: string) {
    if(event.code === 'Backspace' || event.code === 'Delete') {
      const formArray = this.formGroup.get("contactTypesRows") as FormArray<FormGroup>;
      const formArrayValues = formArray.controls[formGroupName];

      const formControl = formArrayValues.get(formControlName);

      if(formControl?.value) {
        formControl.setValue(formControl.value.slice(0, -1));
      }
    }
  }

  createContactTypeRow(contact: ContactDetails): FormGroup {
     
    return new FormGroup({
      order: new FormControl<number>(contact.order),
      contactType: new FormControl<string[]>([contact.contactType ?? '']),
      value: new FormControl<string>(contact.value ?? ''),
      active: new FormControl<boolean>(contact.active ?? false)
    });
  }

  addContactTypeRow() {
    const formArray = this.formGroup.get('contactTypesRows') as FormArray;
    const formValues = formArray.value as ContactDetails[]
    const highestIndex = formValues.reduce(
      (max, contact) => contact.order > max ? contact.order : max,
      formValues[0].order
    );    
    formArray.push(this.createContactTypeRow({
      order: highestIndex + 1,
      contactType: this.contactTypeDefault.contactType,
      value: '',
      active: false
    }));
  }

  sortByOrder() {
    setTimeout(() => {
      const formArray = this.formGroup.get("contactTypesRows") as FormArray<FormGroup>;
      const formGroups = formArray.controls;

      formGroups.sort((a: FormGroup, b: FormGroup) => {
        return a.get("order")?.value > b.get("order")?.value ? 1 : -1;
      });

    }, 100);

  }

  drop(event: CdkDragDrop<any>) {
    const contactTypesGroups = this.formGroup.get('contactTypesRows') as FormArray<FormGroup>;
    const contactTypesRows = contactTypesGroups.controls;
    const formGroupSelected = contactTypesRows[event.previousIndex];
    const formGroupToSwitch = contactTypesRows[event.currentIndex];

    const aux = formGroupSelected.get("order")?.value;
    const currentOrderValue = formGroupToSwitch.get("order")?.value;

    formGroupSelected.get("order")?.setValue(currentOrderValue);
    formGroupToSwitch.get("order")?.setValue(aux);

    this.sortByOrder();

    moveItemInArray(contactTypesRows, event.previousIndex, event.currentIndex);
  }
}
